import type { TFunction } from "i18next"

import type {
  AlertSettingConfigThreshold,
  AlertSettingParam,
  AlertSettingType,
} from "types/alerts.types"

import { getTypeParams } from "helpers/formatters/alertSettingsFormatters"
import { numberToLocale } from "helpers/utils/localeNumber"
import { getFieldUnit } from "helpers/utils/telemetryFields"

// The name is already the accordion's title, and the form leaves out active and send_sms.
const HIDDEN_PARAMS = ["active", "custom_name", "send_sms"]
// Listed under their own titles. Any other param is part of the alert's condition.
const RESOLUTION_PARAMS = ["auto_resolve_timeout"]
const NOTIFICATION_PARAMS = ["notification_policy", "notify_resolved", "send_email"]
const TITLED_PARAMS = [
  "thresholds",
  "fields",
  "note",
  ...RESOLUTION_PARAMS,
  ...NOTIFICATION_PARAMS,
]

interface ConfigurationParams {
  thresholdsParam?: AlertSettingParam
  fieldsParam?: AlertSettingParam
  conditionParams: AlertSettingParam[]
  resolutionParams: AlertSettingParam[]
  notificationParams: AlertSettingParam[]
  noteParam?: AlertSettingParam
}

export const getConfigurationParams = (
  type: string,
  alertTypes: AlertSettingType[],
): ConfigurationParams => {
  const params = getTypeParams(type, alertTypes, HIDDEN_PARAMS) ?? []
  return {
    thresholdsParam: params.find(({ id }) => id === "thresholds"),
    fieldsParam: params.find(({ id }) => id === "fields"),
    conditionParams: params.filter(({ id }) => !TITLED_PARAMS.includes(id)),
    resolutionParams: params.filter(({ id }) => RESOLUTION_PARAMS.includes(id)),
    notificationParams: params.filter(({ id }) => NOTIFICATION_PARAMS.includes(id)),
    noteParam: params.find(({ id }) => id === "note"),
  }
}

// Thresholds are stored in the field's own unit. Fields with different or unknown units
// leave the bounds without one.
export const getThresholdsUnit = (fields: string[]): string | undefined => {
  const units = new Set(fields.map(getFieldUnit))
  const [unit] = units
  return units.size === 1 && unit ? unit : undefined
}

// The threshold form stores an outside range with min above max, so the bounds are read
// back the same way.
export const getThresholdDescription = (
  threshold: AlertSettingConfigThreshold,
  t: TFunction,
  locale = "en",
  unit?: string,
): string => {
  const format = (value: number) => {
    const number = numberToLocale(value, locale) ?? ""
    return unit ? `${number}\u00a0${unit}` : number
  }
  const { min, max } = threshold
  const hasMin = typeof min === "number"
  const hasMax = typeof max === "number"

  if (hasMin && hasMax) {
    return min <= max
      ? t("alert_settings.BETWEEN_VALUES", { min: format(min), max: format(max) })
      : t("alert_settings.OUTSIDE_VALUES", { min: format(max), max: format(min) })
  }
  if (hasMax) return t("alert_settings.BELOW_VALUE", { value: format(max) })
  if (hasMin) return t("alert_settings.ABOVE_VALUE", { value: format(min) })
  return "-"
}
